// src/components/practice/ConfidencePicker.tsx
'use client'

import { useId, useRef, type KeyboardEvent } from 'react'
import { Check, CircleHelp, Dices, ShieldCheck } from 'lucide-react'
import { CONFIDENCE_LABEL, CONFIDENCE_RATINGS, type ConfidenceRating } from '@unlimitedtests/core'
import { cn } from '@/lib/utils'

/**
 * "How sure are you?" — Certain / Unsure / Guess, before feedback on a practice
 * answer (confidence calibration; rules in src/lib/practice/confidence.ts).
 *
 * OPTIONAL BY CONSTRUCTION: nothing is pre-selected, Submit never waits on it,
 * and tapping the chosen option again clears it. It is a radiogroup — one tab
 * stop, arrow keys move and select, Space/Enter toggle — and the chosen option
 * carries a check mark and a heavier border as well as colour, so the state is
 * never colour-only.
 *
 * FIXED LIGHT, like every answer surface (answer-surfaces-force-light): it is
 * its own small white "paper" box, so it reads the same on a themed card in
 * dark mode. No theme tokens, no dark: variants. The options are not
 * disabled-styled buttons, so the global button[disabled] guard never applies.
 *
 * Practice surfaces only. The caller decides whether to render it (the
 * student's setting via useConfidenceShown, and never on an assessment).
 */
export interface ConfidencePickerProps {
  value: ConfidenceRating | null
  onChange: (value: ConfidenceRating | null) => void
  /** Locks the control (while a check is in flight). */
  disabled?: boolean
  className?: string
}

const ICON: Record<ConfidenceRating, typeof ShieldCheck> = {
  CERTAIN: ShieldCheck,
  UNSURE: CircleHelp,
  GUESS: Dices,
}

export default function ConfidencePicker({
  value,
  onChange,
  disabled = false,
  className,
}: ConfidencePickerProps) {
  const labelId = useId()
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  // Roving tab stop: the chosen option, else the first.
  const tabStop = value ? CONFIDENCE_RATINGS.indexOf(value) : 0

  function toggle(r: ConfidenceRating) {
    if (disabled) return
    onChange(value === r ? null : r)
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    if (disabled) return
    const n = CONFIDENCE_RATINGS.length
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % n
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + n) % n
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = n - 1
    if (next === null) return
    e.preventDefault()
    refs.current[next]?.focus()
    onChange(CONFIDENCE_RATINGS[next])
  }

  return (
    <div
      data-testid="confidence-picker"
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-slate-200 bg-white px-2.5 py-2 sm:px-3',
        className,
      )}
    >
      <span id={labelId} className="text-sm text-slate-700">
        How sure are you? <span className="text-xs text-slate-500">(optional)</span>
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="flex flex-wrap gap-1 sm:gap-1.5">
        {CONFIDENCE_RATINGS.map((r, i) => {
          const on = value === r
          const Icon = on ? Check : ICON[r]
          return (
            <button
              key={r}
              ref={(el) => {
                refs.current[i] = el
              }}
              type="button"
              role="radio"
              aria-checked={on}
              aria-disabled={disabled || undefined}
              tabIndex={i === tabStop ? 0 : -1}
              data-confidence={r}
              onClick={() => toggle(r)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className={cn(
                'inline-flex min-h-[36px] items-center gap-1 rounded-full border px-2 py-1 text-sm transition-colors sm:gap-1.5 sm:px-3',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-1',
                on
                  ? 'border-2 border-emerald-700 bg-emerald-50 font-semibold text-emerald-900'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {CONFIDENCE_LABEL[r]}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * The one line under a result for the two cells worth a word (a confident miss,
 * a correct guess — confidenceFeedbackLine in @unlimitedtests/core). Inherits
 * the result panel's colour, so it reads right on any surface. Renders nothing
 * for every other answer.
 */
export function ConfidenceFeedbackLine({
  line,
  className,
}: {
  line: string | null
  className?: string
}) {
  if (!line) return null
  return (
    <p data-testid="confidence-feedback" className={cn('mt-1.5 text-sm', className)}>
      {line}
    </p>
  )
}
